import axios from "axios";
import { useState, useCallback } from "react";
import { hostname } from "../../../utils";
import {
  MentionResult,
  MentionStats,
  ListMentionsParams,
  GenerateRepliesRequest,
  SuggestedReplies,
} from "../types";

export const useMentions = (
  getToken: () => Promise<string>,
  companyId: string
) => {
  const [mentions, setMentions] = useState<MentionResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<MentionStats | null>(null);

  const listMentions = useCallback(
    async (params?: ListMentionsParams) => {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const response = await axios.get(
          `${hostname}/api/v0/mentions`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { companyId, ...params },
          }
        );
        setMentions(response.data.mentions);
        setTotal(response.data.total);
        return response.data;
      } catch (err: any) {
        const errorMsg =
          err.response?.data?.error || err.message || "Failed to load mentions";
        setError(errorMsg);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [getToken, companyId]
  );

  const getMention = useCallback(
    async (mentionId: string) => {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/api/v0/mentions/${mentionId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { companyId },
        }
      );
      return response.data.mention;
    },
    [getToken, companyId]
  );

  const dismissMention = useCallback(
    async (mentionId: string) => {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/api/v0/mentions/${mentionId}/dismiss`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { companyId },
        }
      );
      return response.data;
    },
    [getToken, companyId]
  );

  const generateReplies = useCallback(
    async (mentionId: string, data: GenerateRepliesRequest): Promise<SuggestedReplies> => {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/api/v0/mentions/${mentionId}/generate-replies`,
        data,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { companyId },
        }
      );
      return response.data.replies;
    },
    [getToken, companyId]
  );

  const getStats = useCallback(
    async (monitorId?: string) => {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/api/v0/mentions/stats`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { companyId, monitorId },
        }
      );
      setStats(response.data.stats);
      return response.data.stats;
    },
    [getToken, companyId]
  );

  return {
    mentions,
    loading,
    error,
    total,
    stats,
    listMentions,
    getMention,
    dismissMention,
    generateReplies,
    getStats,
  };
};
